<template>
	<v-card class="directory-page page" flat>
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<div v-else-if="isBlocked !== null">
			<v-skeleton-loader v-if="isDirectoryEmpty" v-bind="attrs" type="card" />
			<v-card-text v-else>
				<v-card class="directory-page__section" flat>
					<div class="directory-page__info-wrapper">
						<Avatar
							:avatar="avatarPath"
							:size="120"
							border-radius="square"
							class="directory-page__avatar mr-4"
						/>

						<div class="directory-page__info">
							<h1 class="directory-page__info-username">
								{{ userName }}
							</h1>
							<div class="d-flex flex-column">
								<span v-if="companyName" class="directory-page__info-company mb-1">
									{{ companyName }}
								</span>
								<span v-if="heldPosition" class="directory-page__info-position">
									{{ heldPosition }}
								</span>
								<span v-if="location" class="directory-page__info-location">
									<Icon
										type="pin"
										color="var(--location-color)"
										:size="14"
										:pr="4"
									/>
									<span>{{ location }}</span>
								</span>
							</div>
						</div>
					</div>
					<ProfileBadge v-if="customFlag" :badge="customFlagProps" />
				</v-card>
				<div v-for="(section, index) in allowSections" :key="index" class="directory-page__section">
					<h3 class="directory-page__section-title">
						{{ section[0].section }}
					</h3>
					<div v-for="field in section" :key="field.field">
						<div v-if="field.type === 'email'" class="directory-page__section-text d-flex align-center flex-nowrap">
							<Icon type="email" color="var(--accent-color)" :pr="12" :size="22" />
							<a :href="'mailto:' + field.value" style="text-decoration: none; color: var(--accent-color);">
								{{ field.value }}
							</a>
						</div>
						<div v-else-if="socialFieldTypes.some(type => type === field.type)">
							<a
								:href="field.value"
								target="_blank"
								class="directory-page__network-link"
							>
								<h3 class="directory-page__network-title">
									{{ field.title }}
								</h3>
								<div class="d-flex align-center">
									<Icon 
										:size="28" 
										:type="networkFieldType(field.type)" 
									/>
									<span class="directory-page__network-link-text ml-2">
										{{ field.value }}
									</span>
								</div>
							</a>
						</div>
						<div v-else-if="field.type === 'phone'" class="py-3 d-flex">
							<Icon type="phone" color="var(--accent-color)" :pr="12" :size="22" />
							<a
								@click="copyPhone(field.value)"
								:href="null"
								style="text-decoration: none; color: var(--ceil-detail-text-color)"
							>
								{{ field.value }}
							</a>
						</div>
						<v-card-text v-else-if="field.type === 'html'" v-html="field.value" />
						<div v-else-if="field.section !== 'Network'" class="mb-2">
							<span class="directory-page__section-label"> 
								{{ field.title }}: 
							</span>
							<span class="directory-page__section-text">
								{{ field.value }}
							</span>
						</div>
					</div>
				</div>
			</v-card-text>
		</div>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Avatar from '@/components/Avatar'
import PageBlocked from '@/components/PageBlocked'
import ProfileBadge from '@/components/ProfileBadge.vue'
import Block from '@/mixins/Block.js'

import _ from 'lodash'

export default {
	name: 'DirectoryPage',
	mixins: [Block],
	components: {
		PageBlocked,
		Avatar,
		ProfileBadge
	},
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated']),
		...mapGetters('directory', ['pageDirectory', 'directory']),
		...mapGetters('event', ['attendee']),
		...mapGetters('app', ['isShowAlert', 'alertMessage']),

		isDirectoryEmpty() {
			const { directory_id: directoryId } = this.directory || {}
			return directoryId !== +this.$route.params.id
		},

		allowSections() {
			return (this.directory.public || [])
				.filter((field) => field.value)
				.reduce((acc, current) => {
					if (acc.length === 0 || acc[acc.length - 1][0].section !== current.section) {
						acc.push([current])
					} else {
						acc[acc.length - 1].push(current)
					}
					return acc
				}, [])
		},
		location() {
			const { directory_forth_row: location } = this.directory || {}
			return location
		},
		userName() {
			const { directory_first_row: firstName } = this.directory || {}
			return `${firstName}`
		},
		avatarPath() {
			const { directory_logo: logo } = this.directory || {}
			return logo
		},
		heldPosition() {
			const { directory_third_row: heldPosition } = this.directory || {}
			return heldPosition
		},
		companyName() {
			const { directory_second_row: company } = this.directory || {}
			return company
		},
		customFlag() {
			const { custom_flag: value } = this.directory || {}
			return value
		},
		customFlagProps() {
			const { custom_flag_color: flag, custom_flag_name: name } = this.directory || {};
			return { custom_bg_color: flag, custom_text_color: '#fff', flag_name: name }
		}
	},
	methods: {
		...mapActions('app', ['showAlert', 'hideAlert']),
		...mapActions('directory', ['getDirectory', 'clearDirectory']),

		copyPhone(value) {
			if (!this.isShowAlert) {
				const phone = value.replace(/\D/g, '')
				this.$copyText(phone).then(() => {
					const closeTimeout = setTimeout(() => {
						this.hideAlert('')
					}, 3000)
					this.showAlert({ message: 'Phone number copied successfully.', closeTimeout })
				})
			}
		},
		networkFieldType(type) {
			return type === 'url' ? 'link' : type
		},
	},
	data() {
		return {
			attrs: {
				elevation: 0,
			},
			socialFieldTypes: [
				'linkedin',
				'facebook',
				'twitter',
				'custom_url',
				'url'
			],
			pageBlockedName: 'directory',
		}
	},
	created() {
		if (this.$route.params.id && (!this.isBlocked || this.isBlocked === null)) {
			this.getDirectory(+this.$route.params.id)
		}
	},
	beforeDestroy() {
		this.clearDirectory()
	},
}
</script>

<style lang="scss">
.directory-page {
	background-color: transparent !important;

	&__section {
		max-width: 600px;
		border-radius: 10px !important;
		margin-bottom: 8px;
		padding: 16px;
		background-color: #fff;
		font-family: Montserrat, sans-serif;

		&-title {
			font-size: 16px;
			font-weight: 700;
			line-height: 19.5px;
			color: #4a4a4a;
			margin-bottom: 12px;
		}

		&-label {
			font-size: 14px;
			font-weight: 700;
			line-height: 17.07px;
			color: var(--accent-color);
			margin-right: 12px;
		}

		&-text {
			font-size: 14px;
			line-height: 17.07px;
			color: #697583;
		}
	}

	&__network {
		&-link {
			text-decoration: none;
			display: block;
			margin-bottom: 12px;

			&-text {
				font-size: 14px;
				line-height: 17.07px;
				color: var(--accent-color);
				width: 100%;
				overflow: hidden;
				text-overflow: ellipsis;
				display: -webkit-box;
				-webkit-line-clamp: 1;
				-webkit-box-orient: vertical;
			}
		}

		&-title {
			font-size: 14px;
			font-weight: 600;
			line-height: 22px;
			color: #4a4a4a;
			margin-bottom: 4px;
		}
	}

	&__info {
		font-family: Montserrat, sans-serif;
		width: 100%;

		&-wrapper {
			display: flex;
			flex-wrap: nowrap;

			@media (max-width: 480px) {
				flex-direction: column;
				align-items: center;
			}
		}

		&-username {
			font-size: 24px;
			line-height: 29.26px;
			font-weight: 700;
			color: var(--accent-color);
			margin-bottom: 12px;

			@media (max-width: 480px) {
				margin-top: 12px;
				text-align: center;
			}
		}

		&-company,
		&-position {
			font-size: 16px;
			line-height: 19.5px;
			color: #4a4a4a;
			width: 100%;
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 1;
			-webkit-box-orient: vertical;

			@media (max-width: 480px) {
				text-align: center;
			}
		}

		&-location {
			font-size: 13px;
			line-height: 19.37px;
			font-weight: 500;
			color: var(--location-color);
			display: flex;
			align-items: center;
			flex-wrap: nowrap;
			margin-top: 12px;

			@media (max-width: 480px) {
				justify-content: center;
			}
		}
	}

}
</style>
